import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Modal,
  Radio,
  notification,
} from 'tea-component';
import moment from 'moment';
import LiveMonthFilter from '@sg/components/LiveMonthFilter';
import { useDataTimeRange, type TDurationRange } from './useDataTimeRange';
import {
  STUDIOS_DOWNLOAD_FILE_FORMAT_OPTIONS,
  type IMetricsDownloadConfig,
  type TMetricsDownloadFileFormat,
  type TMetricsDownloadMetricKey,
  type TDimensionKey,
} from './config';
import { exportMetrics, MetricsDownloadError } from './utils/exportMetrics';
import './index.scss';

interface IMetricsDownloadModalBaseProps {
  visible: boolean;
  config: IMetricsDownloadConfig;
  activeDateTypeSegment: string;
  selectedMonth?: string | null;
  onClose: () => void;
  /** Modal size (width in px). Defaults to 700 for Studios, 500 for Portfolio. */
  modalSize?: number;
  /** Custom modal caption. Defaults to "Download Settings". */
  caption?: string;
  /** Default file format selection. Defaults to "excel". */
  defaultFileFormat?: TMetricsDownloadFileFormat;
}

type IMetricsDownloadModalProps = IMetricsDownloadModalBaseProps & (
  | { config: IMetricsDownloadConfig & { scope: 'studio' }; studioId: string; studioName: string }
  | { config: IMetricsDownloadConfig & { scope: 'portfolio' }; studioId?: never; studioName?: never }
  | { studioId?: string; studioName?: string }
);

const MetricsDownloadModal: React.FC<IMetricsDownloadModalProps> = ({
  visible,
  config,
  studioId,
  studioName,
  activeDateTypeSegment,
  selectedMonth,
  onClose,
  modalSize = 700, // Default to 700 for Studios
  caption,
  defaultFileFormat = 'excel',
}) => {
  const [selectedMetrics, setSelectedMetrics] = useState<TMetricsDownloadMetricKey[]>(
    config.defaultMetrics,
  );
  const [selectedDimensions, setSelectedDimensions] = useState<TDimensionKey[]>(
    config.defaultDimensions ?? [],
  );
  const [fileFormat, setFileFormat] = useState<TMetricsDownloadFileFormat>(
    defaultFileFormat,
  );
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Fetch available date range with module-level cache + TTL + deduplication.
  // Prevents redundant getDataTime API calls when modal is repeatedly opened/closed.
  const {
    availableDateRange,
    durationRange,
    setDurationRange,
  } = useDataTimeRange({
    visible,
    scope: config.scope,
    studioId,
    selectedMonth,
  });

  // Reset form each open so consecutive downloads start from defaults.
  useEffect(() => {
    if (!visible) {
      return;
    }
    setSelectedMetrics(config.defaultMetrics);
    setSelectedDimensions(config.defaultDimensions ?? []);
    setFileFormat(defaultFileFormat);
    setConfirmLoading(false);
  }, [visible, config.defaultMetrics, config.defaultDimensions, defaultFileFormat]);

  // Metrics are required only when metricOptions exist; dimensions are always optional.
  // Portfolio & Finance scopes do not require studioId (e.g. LiveFinance).
  const hasMetricOptions = config.metricOptions.length > 0;
  const canConfirm = (!hasMetricOptions || selectedMetrics.length > 0)
    && !confirmLoading
    && (config.scope === 'portfolio' || config.scope === 'finance' || !!studioId)
    && !!durationRange;

  const handleDurationChange = (value: TDurationRange): void => {
    if (!Array.isArray(value) || !value[0] || !value[1]) {
      return;
    }
    setDurationRange(value);
  };

  const handleConfirm = async (): Promise<void> => {
    if (!canConfirm) {
      return;
    }

    setConfirmLoading(true);
    try {
      const startDate = moment(durationRange[0]).format('YYYY-MM');
      const endDate = moment(durationRange[1]).format('YYYY-MM');

      await exportMetrics({
        metrics: selectedMetrics,
        scope: config.scope,
        studioId,
        studioName,
        activeDateTypeSegment,
        // Picker returns YYYY-MM-DD; APIs expect YYYY-MM.
        startDate,
        endDate,
        fileFormat,
        // 外部页面时间筛选器的值（YYYY-MM）
        datatime: selectedMonth ?? '',
        dimensions: selectedDimensions,
      });
      notification.success({
        description: 'Download Completed',
      });
      onClose();
    } catch (error) {
      console.error('Metrics download failed:', error);
      // Prefer metric labels from MetricsDownloadError (e.g. "Burn Rate, Headcount").
      const failedLabels = error instanceof MetricsDownloadError
        ? error.failedMetrics.join(', ')
        : '';
      notification.error({
        description: failedLabels
          ? `Failed to download: ${failedLabels}. Please try again.`
          : 'Failed to download selected metrics. Please try again.',
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      caption={caption ?? 'Download Settings'}
      size={modalSize}
      onClose={onClose}
      className="dbs-studios-metrics-download-modal"
    >
      <Modal.Body>
        <div className="dbs-studios-metrics-download-modal__form">
          <div className="dbs-studios-metrics-download-modal__row">
            <div className="dbs-studios-metrics-download-modal__label">Duration</div>
            <LiveMonthFilter
              {...({
                value: durationRange,
                onChange: handleDurationChange,
                style: { marginRight: 0 },
                className: 'dbs-studios-metrics-download-modal__duration-picker',
                overlayClassName: 'dbs-month-picker-overlay',
                dateRange: availableDateRange,
              } as any)}
            />
          </div>

          {config.metricOptions.length > 0 && (
            <div className="dbs-studios-metrics-download-modal__row">
              <div className="dbs-studios-metrics-download-modal__label">Metrics</div>
              <Checkbox.Group
                value={selectedMetrics}
                onChange={(value: string[]) => {
                  setSelectedMetrics(value as TMetricsDownloadMetricKey[]);
                }}
                className="dbs-studios-metrics-download-modal__metrics-group"
              >
                {config.metricOptions.map(option => (
                  <Checkbox key={option.key} name={option.key}>
                    {option.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </div>
          )}

          {config.dimensionOptions && config.dimensionOptions.length > 0 && (
            <div className="dbs-studios-metrics-download-modal__row">
              <div className="dbs-studios-metrics-download-modal__label">Dimension (Optional)</div>
              <Checkbox.Group
                value={selectedDimensions}
                onChange={(value: string[]) => {
                  setSelectedDimensions(value);
                }}
                className="dbs-studios-metrics-download-modal__metrics-group"
              >
                {config.dimensionOptions.map(option => (
                  <Checkbox key={option.key} name={option.key}>
                    {option.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </div>
          )}

          <div className="dbs-studios-metrics-download-modal__row">
            <div className="dbs-studios-metrics-download-modal__label">File Format</div>
            <Radio.Group
              value={fileFormat}
              onChange={(value: string) => {
                setFileFormat(value as TMetricsDownloadFileFormat);
              }}
               className="dbs-studios-metrics-download-modal__metrics-group"
            >
              {STUDIOS_DOWNLOAD_FILE_FORMAT_OPTIONS.map(option => (
                <Radio key={option.key} name={option.key}>
                  {option.label}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          className="dbs-gradient-btn"
          loading={confirmLoading}
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          Confirm
        </Button>
        <Button type="weak" onClick={onClose} disabled={confirmLoading}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MetricsDownloadModal;

export {
  STUDIOS_METRICS_DOWNLOAD_CONFIG,
  PORTFOLIO_METRICS_DOWNLOAD_CONFIG,
  LIVE_FINANCE_DOWNLOAD_CONFIG,
  STUDIOS_DOWNLOAD_METRIC_OPTIONS,
  STUDIOS_DOWNLOAD_DEFAULT_METRICS,
  STUDIOS_DOWNLOAD_FILE_FORMAT_OPTIONS,
} from './config';
export type {
  IMetricsDownloadConfig,
  TMetricsDownloadMetricKey,
  TMetricsDownloadFileFormat,
  TMetricsDownloadScope,
  TDimensionKey,
  IDimensionOption,
  TStudiosDownloadMetricKey,
  TStudiosDownloadFileFormat,
} from './config';

